import { type QueryClient, useMutation, useQuery } from '@tanstack/react-query'
import axios from 'axios'
import pluralize from 'pluralize'
import { createContext, useContext } from 'react'
import { toast } from 'sonner'

import { storedAttribution } from '../lib/attribution'
import { CacheKeys } from '../lib/cache'
import type { ClientContext } from '../lib/checkoutEvidence'
import { DotStepService, trackFrontDoorDotResult } from '../lib/frontDoorConversion'
import { CamelToSnakeCaseNested, toCamelCase, toSnakeCase } from '../lib/typeHelper'
import type { Chat, ChatActionInterface, ChatActionWithValue, ChatContent } from './chat'
export type { ChatActionInterface, ChatActionWithValue } from './chat'
import { Business } from './business'
import type { DocumentType } from './document'
import type { Driver } from './driver'
import { type Trailer, type Truck } from './equipment'
import type { CoverageType, EnabledCoverage } from './insuranceCoverage'
import type { InsuranceEstimate } from './insuranceEstimate'
import type { InsuranceIndication } from './insuranceIndication'
import type { InsurancePolicy } from './insurancePolicy'
import type { InsuranceQuote } from './insuranceQuote'
import { Vendor } from './insuranceTypes'
import type { BusinessOwner, CommodityItem } from './user'

export const InsuranceApplicationUserIdContext = createContext<string | undefined>(undefined)

export function useInsuranceApplicationUserId() {
  return useContext(InsuranceApplicationUserIdContext)
}

function userIdParams(userId?: string) {
  return userId ? { params: { user_id: userId } } : undefined
}

interface UseInsuranceApplication {
  /** Unset while the caller is still loading which application it wants. */
  id?: string
  enabled?: boolean
}

export function useInsuranceApplication({ enabled = true, id }: UseInsuranceApplication) {
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: [CacheKeys.APPLICATION, id],
    queryFn: ({ signal }) => getInsuranceApplication(id!, signal),
    enabled: enabled && !!id,
  })

  return {
    data,
    isLoading,
    isFetching,
    refetch,
  }
}

export interface IndicationCheckout {
  indicationIds: string[]
  /** The transcript entry for what the customer picked. */
  content?: ChatContent
  clientId?: string
  dom_html?: string
  dom_screenshot_b64?: string
  client_context?: ClientContext
}

interface UseInsuranceApplicationMutation {
  application: InsuranceApplication
  onSettled?: () => void
}

export function useInsuranceApplicationMutation({ application, onSettled }: UseInsuranceApplicationMutation) {
  const userId = useInsuranceApplicationUserId()
  return useMutation({
    mutationFn: (data: ChatActionWithValue) => {
      const service = data.service || application.action.service
      return insuranceApplicationAction(
        application.id,
        {
          service,
          content: toSnakeCase(data.content),
          client_id: data.clientId,
          value: data.value,
          ...(data.documentIds && data.documentIds.length > 0 ? { document_ids: data.documentIds } : {}),
          ...(data.attestations ? { attestations: data.attestations } : {}),
          ...(data.bind_attestations ? { bind_attestations: data.bind_attestations } : {}),
          ...(data.dom_html ? { dom_html: data.dom_html } : {}),
          ...(data.dom_screenshot_b64 ? { dom_screenshot_b64: data.dom_screenshot_b64 } : {}),
          ...(data.client_context ? { client_context: data.client_context } : {}),
          // The chat's DOT step is the application-start form's DOT entry.
          ...(service === DotStepService.ADD ? { attribution: storedAttribution() } : {}),
        },
        userId
      )
    },
    onSuccess: (updated, data) => {
      // Staff acting for the customer must not fire the customer's ad conversion.
      if (!userId && (data.service || application.action.service) === DotStepService.ADD) {
        trackFrontDoorDotResult(updated, String(data.value))
      }
    },
    onSettled,
  })
}

function uploadedFiles(data: FormData): string {
  return pluralize('file', data.getAll('file').length, true)
}

function uploadErrorMessage(error: unknown): string {
  // The load balancer refuses an oversized body before Django sees it.
  if ((error as { response?: { status?: number } } | undefined)?.response?.status === 413) {
    return 'Those files are too large to upload at once. Try fewer or smaller files.'
  }
  return "We couldn't upload your files. Please try again."
}

export function useInsuranceApplicationFormDataMutation({
  application,
  onSettled,
}: UseInsuranceApplicationMutation) {
  const userId = useInsuranceApplicationUserId()
  return useMutation({
    mutationFn: (data: FormData) => {
      return insuranceApplicationActionWithFile(application.id, data, userId)
    },
    // The chat hides the upload step while this runs, so the toast is the only sign it is still going.
    onMutate: (data) => ({ toastId: toast.loading(`Uploading ${uploadedFiles(data)}…`) }),
    onSuccess: (_, data, context) => {
      toast.success(`${uploadedFiles(data)} uploaded`, { id: context.toastId })
    },
    onError: (error, _, context) => {
      toast.error(uploadErrorMessage(error), { id: context?.toastId })
    },
    onSettled,
  })
}

export interface EditChatItemPayload extends ChatActionWithValue {
  chatItemId: string
}

export function useEditChatItemMutation({ application, onSettled }: UseInsuranceApplicationMutation) {
  const userId = useInsuranceApplicationUserId()
  return useMutation({
    mutationFn: (data: EditChatItemPayload) => {
      return editChatItemAction(
        application.id,
        {
          chat_item_id: data.chatItemId,
          service: data.service || '',
          content: toSnakeCase(data.content),
          value: data.value,
        },
        userId
      )
    },
    onSettled,
  })
}

export interface InsuranceApplicationProgress {
  label: string
  status: 'COMPLETED' | 'IN_PROGRESS' | 'LOCKED'
}

export enum InsuranceApplicationSource {
  WEB = 'WEB',
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

export interface InsuranceApplicationMetadata {
  currentInsurance?: {
    insurer: string
    expirationDate: string | null
  }
  [key: string]: unknown
}

export interface ConnectedTelematics {
  /** PRE_BIND or POST_BIND. */
  type: string
  /** Integration used to connect, e.g. TERMINAL. */
  provider: string
  /** The customer's own provider, e.g. "samsara" — what we name in the UI. */
  providerType: string
}

export interface PortalLink {
  url: string
  sourceLabel: string
  statusLabel: string
  providerLabel: string
}

/** A quote the customer picked by an email or SMS reply, held until they confirm their information in the app. */
export interface PendingChannelPick {
  indicationIds: string[]
  /** Carrier names for the picked indications, e.g. ["Progressive"]. */
  vendors: string[]
  channel: 'EMAIL' | 'SMS'
  /** ISO datetime of the reply. */
  at: string
}

export enum RocklakeLineState {
  NOT_PICKED = 'NOT_PICKED',
  READY = 'READY',
  SENT = 'SENT',
}

export enum InsurancePackageSource {
  STAFF = 'STAFF',
  SYSTEM = 'SYSTEM',
}

export interface InsurancePackage {
  id: string
  source: InsurancePackageSource
  /** Whole offers: every coverage on each of them is in the package. */
  indicationIds: string[]
  dateCreated: string
  /** Why the customer no longer sees it. Only staff are sent a withdrawn package, so they can delete it. */
  withdrawnReason: string | null
}

export interface InsuranceApplication {
  id: string
  orgId: string
  dateCreated: string
  dateUpdated: string
  name: string
  source: InsuranceApplicationSource
  progress: ReadonlyArray<InsuranceApplicationProgress>
  /** 0–100, advancing with each question; computed by the backend. */
  percentComplete: number
  chat: Chat
  action: ChatActionInterface
  nextService: string
  lastUsedService: string
  indications: InsuranceIndication[]
  /** The offers staff or the system put together for this customer; shown first on /quotes. */
  package?: InsurancePackage | null
  estimates: InsuranceEstimate[]
  quotes: InsuranceQuote[]
  insurancePolicies: InsurancePolicy[]
  commodities: CommodityItem[]
  business?: Business | null
  businessOwner: BusinessOwner | null
  entityType?: string | null
  hasEld?: boolean | null
  eldProvider?: string | null
  metadata?: InsuranceApplicationMetadata
  /** A contractor's answered questions, in asking order. */
  contractorAnswers?: ContractorAnswer[]
  /** The coverages this business can choose (a contractor's menu differs from trucking's). */
  coverageMenu?: EnabledCoverage[]
  portalLinks?: PortalLink[]
  /** True once coverage selection completed and carriers were asked for prices. */
  hasDispatchedIndications: boolean
  /** Carriers already asked for a price whose indication rows don't exist yet. */
  awaitingCarrierCount: number
  /** Connected telematics for the org — empty when nothing is wired up. */
  telematics: ConnectedTelematics[]
  /**
   * ISO datetime the application was first edited after carriers last priced it, so the
   * quotes on screen are stale. Null once a dispatch answers it.
   */
  dateEditedSinceQuote?: string | null
  /** ISO datetime the customer last asked their sales owner for fresh prices. */
  dateRequoteRequested?: string | null
  /** Staff only: no direct Progressive card yet, and the carrier is in a state Progressive quotes. */
  progressiveNotRun: boolean
  /** Staff only: where each Rocklake line staff send by hand stands, keyed by coverage. Empty until carriers were asked. */
  rocklakeManualLines: Partial<Record<CoverageType, RocklakeLineState>>
  /** Carriers were asked and the customer hasn't confirmed what was filled in: /quotes shows the review step first. */
  needsReviewAttestation: boolean
  /** A quote the customer picked by email or SMS while that confirmation was outstanding. */
  pendingChannelPick: PendingChannelPick | null
  /** The first edit after a carrier was last asked; never cleared. Staff compare it to a Corgi draft's last submit. */
  dateLastEdited?: string | null
}

function getInsuranceApplication(id: string, signal?: AbortSignal): Promise<InsuranceApplication> {
  return axios
    .get(`/api/v1/insure/insurance-application/${id}/`, { signal })
    .then((response) => toCamelCase(response.data) as InsuranceApplication)
}

/** Reads the application into its cache now, so the routes deciding where it belongs see it as it stands. */
export function refreshInsuranceApplication(
  queryClient: Pick<QueryClient, 'fetchQuery'>,
  id: string
): Promise<InsuranceApplication> {
  return queryClient.fetchQuery({
    queryKey: [CacheKeys.APPLICATION, id],
    queryFn: () => getInsuranceApplication(id),
    staleTime: 0,
  })
}

export interface LossRunUploadResult {
  documentId: string
  /** False when the carrier refused the file or has no document endpoint — it is still stored. */
  sentToCarrier: boolean
  /** True when the carrier takes this file only from Cai's staff, who send it on. */
  handledByStaff: boolean
}

/** Upload a loss run for the carrier that is holding this offer for one. */
export function uploadDocumentForIndication(
  applicationId: string,
  indicationId: string,
  file: File,
  documentType: DocumentType
): Promise<LossRunUploadResult> {
  const form = new FormData()
  form.append('file', file)
  form.append('indication_id', indicationId)
  form.append('document_type', documentType)
  return axios
    .post(`/api/v1/insure/insurance-application/${applicationId}/upload-carrier-document/`, form)
    .then((response) => toCamelCase(response.data) as LossRunUploadResult)
}

/** `coverageLine` sends that one Rocklake line instead of the whole vendor. */
export function resubmitIndications(
  id: string,
  vendor: Vendor,
  force = false,
  coverageLine?: CoverageType
): Promise<InsuranceApplication> {
  return axios
    .post(`/api/v1/insure/insurance-application/${id}/resubmit-indications/`, {
      vendor,
      force,
      ...(coverageLine ? { coverage_line: coverageLine } : {}),
    })
    .then((response) => toCamelCase(response.data) as InsuranceApplication)
}

/** Seconds since the last send when the backend refused a fast repeat, else null. */
export function dispatchBlockedSecondsAgo(error: unknown): number | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return null
  return Number(error.response.data?.last_dispatched_seconds_ago ?? 0)
}

export function insuranceApplicationAction(
  id: string,
  data: {
    service: string
    content?: CamelToSnakeCaseNested<ChatContent>
    value: string | number | Record<string, any>
    client_id?: string
    document_ids?: string[]
  },
  userId?: string
): Promise<InsuranceApplication> {
  return axios
    .post(`/api/v1/insure/insurance-application/${id}/process-action/`, data, userIdParams(userId))
    .then((response) => toCamelCase(response.data) as InsuranceApplication)
}

/** The quotes page's checkout. Its own endpoint: the browser has no business naming
 *  the step the application is on just to reach it. */
export function useIndicationCheckout(applicationId: string, onSettled?: () => void) {
  const userId = useInsuranceApplicationUserId()
  return useMutation({
    onSettled,
    mutationFn: (data: IndicationCheckout) =>
      axios
        .post(
          `/api/v1/insure/insurance-application/${applicationId}/checkout/`,
          {
            indication_ids: data.indicationIds,
            ...(data.content ? { content: toSnakeCase(data.content) } : {}),
            ...(data.clientId ? { client_id: data.clientId } : {}),
            ...(data.dom_html ? { dom_html: data.dom_html } : {}),
            ...(data.dom_screenshot_b64 ? { dom_screenshot_b64: data.dom_screenshot_b64 } : {}),
            ...(data.client_context ? { client_context: data.client_context } : {}),
          },
          userIdParams(userId)
        )
        .then((response) => toCamelCase(response.data) as InsuranceApplication),
  })
}

export function insuranceApplicationActionWithFile(
  id: string,
  data: FormData,
  userId?: string
): Promise<InsuranceApplication> {
  return axios
    .post(`/api/v1/insure/insurance-application/${id}/process-upload-action-with-file/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...(userId ? { params: { user_id: userId } } : {}),
    })
    .then((response) => toCamelCase(response.data) as InsuranceApplication)
}

export function editChatItemAction(
  id: string,
  data: {
    chat_item_id: string
    service: string
    content?: CamelToSnakeCaseNested<ChatContent>
    value: string | number | Record<string, any> | string[]
  },
  userId?: string
): Promise<InsuranceApplication> {
  return axios
    .post(`/api/v1/insure/insurance-application/${id}/edit-action/`, data, userIdParams(userId))
    .then((response) => toCamelCase(response.data) as InsuranceApplication)
}

export interface DotEditPrefill {
  dotNumber: string
  businessName: string
  address1: string
  city: string
  state: string
  zip: string
  ownerFirstName: string
  ownerLastName: string
  ownerEmail: string
  ownerPhone: string
}

export function lookupDotForEdit(
  applicationId: string,
  dotNumber: string,
  userId?: string
): Promise<DotEditPrefill> {
  return axios
    .post(
      `/api/v1/insure/insurance-application/${applicationId}/lookup-dot/`,
      { dot_number: dotNumber },
      userIdParams(userId)
    )
    .then((response) => toCamelCase(response.data) as DotEditPrefill)
}

export interface EditDotNumberPayload extends DotEditPrefill {
  chatItemId: string
}

export function editDotNumber(
  applicationId: string,
  data: EditDotNumberPayload,
  userId?: string
): Promise<InsuranceApplication> {
  return axios
    .post(
      `/api/v1/insure/insurance-application/${applicationId}/edit-dot/`,
      toSnakeCase(data),
      userIdParams(userId)
    )
    .then((response) => toCamelCase(response.data) as InsuranceApplication)
}

export function useEditDotNumberMutation({ application, onSettled }: UseInsuranceApplicationMutation) {
  const userId = useInsuranceApplicationUserId()
  return useMutation({
    mutationFn: (data: EditDotNumberPayload) => editDotNumber(application.id, data, userId),
    onSettled,
  })
}

export function deleteApplication(id: string) {
  return axios.post(`/api/v1/insure/insurance-application/${id}/delete/`)
}

export function backApplication(id: string) {
  return axios.post(`/api/v1/insure/insurance-application/${id}/back/`)
}

export interface UploadDriversResponse {
  documentId: string
  drivers: Driver[]
  /** Set when part (or all) of the file could not be read; shown to the user. */
  warning?: string
}

export function uploadDrivers(orgId: string, file: File): Promise<UploadDriversResponse> {
  const formData = new FormData()
  formData.append('file', file)
  return axios
    .post('/api/v1/insure/parser/driver/', formData, { params: { org_id: orgId } })
    .then((response) => toCamelCase(response.data) as UploadDriversResponse)
}

export interface UploadTrucksResponse {
  documentId: string
  trucks: Truck[]
  /** Set when part (or all) of the file could not be read; shown to the user. */
  warning?: string
}

export function uploadTrucks(orgId: string, file: File): Promise<UploadTrucksResponse> {
  const formData = new FormData()
  formData.append('file', file)
  return axios
    .post('/api/v1/insure/parser/truck/', formData, { params: { org_id: orgId } })
    .then((response) => toCamelCase(response.data) as UploadTrucksResponse)
}

export interface UploadTrailersResponse {
  documentId: string
  trailers: Trailer[]
  /** Set when part (or all) of the file could not be read; shown to the user. */
  warning?: string
}

export function uploadTrailers(orgId: string, file: File): Promise<UploadTrailersResponse> {
  const formData = new FormData()
  formData.append('file', file)
  return axios
    .post('/api/v1/insure/parser/trailer/', formData, { params: { org_id: orgId } })
    .then((response) => toCamelCase(response.data) as UploadTrailersResponse)
}

export function fetchSandboxDot(): Promise<string> {
  return axios
    .get(`/api/v1/insure/insurance-application/sandbox-dot/`)
    .then((response) => response.data.dot as string)
}

/** The quotes-page clicks the backend counts toward the Slack engagement alerts. */
export enum QuoteEngagementKind {
  CARD_OPENED = 'CARD_OPENED',
  SECTION_OPENED = 'SECTION_OPENED',
  CART_ADDED = 'CART_ADDED',
}

export interface QuoteEngagement {
  kind: QuoteEngagementKind
  section?: string
}

/** Fire-and-forget: a lost click only costs an alert, so it must never surface to the customer. */
export function postQuoteEngagement(
  applicationId: string,
  indicationId: string,
  engagement: QuoteEngagement
) {
  axios
    .post(`/api/v1/insure/insurance-application/${applicationId}/quote-engagement/`, {
      indication_id: indicationId,
      ...engagement,
    })
    .catch(() => undefined)
}

export interface TimeOnPageReport {
  /** One per visit to the application pages. */
  pageViewId: string
  /** Everything the visit has counted so far, so a retried or late report adds nothing. */
  seconds: number
  quotesSeen: boolean
}

/**
 * Counts the customer's time on page toward their time to quoting; resolves false once there's
 * nothing more to count. Sent with keepalive so a report made as the tab closes still arrives.
 */
export function postTimeOnPage(applicationId: string, report: TimeOnPageReport): Promise<boolean> {
  return axios
    .post<{ running: boolean }>(
      `/api/v1/insure/insurance-application/${applicationId}/time-on-page/`,
      { page_view_id: report.pageViewId, seconds: report.seconds, quotes_seen: report.quotesSeen },
      { adapter: 'fetch', fetchOptions: { keepalive: true } }
    )
    .then((response) => response.data.running)
}

/** How long a customer was on the page before their first priced quote; quotesSeenAt is null while it runs. */
export interface TimeToQuotingSummary {
  timeOnPageSeconds: number
  elapsedSeconds: number
  startedAt: string
  quotesSeenAt: string | null
}

/** Staff only. Resolves null for a customer whose onboarding was never timed. */
export function useCustomerTimeToQuoting(orgId: string) {
  return useQuery({
    queryKey: [CacheKeys.ORGANISATIONS, orgId, 'time-to-quoting'],
    queryFn: () =>
      axios
        .get(`/api/v1/insure/organisations/${orgId}/time-to-quoting/`)
        .then((response) => (response.data ? (toCamelCase(response.data) as TimeToQuotingSummary) : null)),
  })
}

export interface ContractorAnswer {
  key: string
  question: string
  answer: string
}
